/**
 * InteractiveDemo Component
 * 
 * Embeds an interactive playable story directly on the homepage.
 * Loads the specified story and provides a limited demo experience
 * (e.g., 5 exchanges before prompting signup).
 * 
 * For the full experience, users will be redirected to the actual story page.
 */

import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import { Icon, Loader } from 'semantic-ui-react';

import { db } from '../firebaseConfig';
import { EnvironmentContext } from '../Context/EnvironmentContext';

import './InteractiveDemo.css';

// Maximum free exchanges in the demo
const MAX_DEMO_EXCHANGES = 5;

export default function InteractiveDemo({ storyId = 'Za2kgJ5eqkuBETwf1CMg' }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const env = useContext(EnvironmentContext);
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [storyData, setStoryData] = useState(null);
    const [showLimitModal, setShowLimitModal] = useState(false);
    
    const iframeRef = useRef(null);

    // Fetch story metadata
    useEffect(() => {
        const fetchStory = async () => {
            try {
                setLoading(true);
                const storyRef = doc(db, 'stories', storyId);
                const docSnap = await getDoc(storyRef);
                
                if (docSnap.exists()) {
                    setStoryData({
                        id: docSnap.id,
                        ...docSnap.data()
                    });
                } else {
                    setError('Story not found');
                }
            } catch (err) {
                console.error('Error fetching story:', err);
                setError('Failed to load demo');
            } finally {
                setLoading(false);
            }
        };
        
        fetchStory();
    }, [storyId]);

    const handlePlayFull = () => {
        navigate(`/Story/${storyId}`);
    };

    const handleSignUp = () => {
        navigate('/SignUp');
    };

    if (loading) {
        return (
            <div className="interactive-demo-loading">
                <Loader active inverted size="large">
                    {t('demo.loading')}
                </Loader>
            </div>
        );
    }

    if (error) {
        return (
            <div className="interactive-demo-error">
                <Icon name="exclamation triangle" size="huge" />
                <p>{error}</p>
            </div>
        );
    }

    const storyName = storyData?.parameters?.name || 'Interactive Demo';
    const storyDescription = storyData?.parameters?.description || '';
    const storyThumbnail = storyData?.parameters?.thumbnail;

    return (
        <div className="interactive-demo">
            {/* Story info header */}
            <div className="interactive-demo-header">
                <div className="interactive-demo-info">
                    {storyThumbnail && (
                        <img 
                            src={storyThumbnail} 
                            alt={storyName}
                            className="interactive-demo-thumbnail"
                        />
                    )}
                    <div className="interactive-demo-meta">
                        <h3 className="interactive-demo-title">{storyName}</h3>
                        <p className="interactive-demo-description">
                            {storyDescription.slice(0, 150)}
                            {storyDescription.length > 150 ? '...' : ''}
                        </p>
                    </div>
                </div>
                <button 
                    className="interactive-demo-play-btn"
                    onClick={handlePlayFull}
                >
                    <Icon name="play" />
                    {t('demo.playFullStory')}
                </button>
            </div>

            {/* Embedded story iframe */}
            <div className="interactive-demo-frame-container">
                <iframe
                    ref={iframeRef}
                    src={`/Story/${storyId}?demo=true`}
                    className="interactive-demo-iframe"
                    title={storyName}
                    allow="fullscreen"
                />
                
                {/* Gradient overlay at bottom to hint at more content */}
                <div className="interactive-demo-fade-overlay" />
            </div>

            {/* CTA Section */}
            <div className="interactive-demo-cta">
                <p className="interactive-demo-cta-text">
                    {t('demo.ctaText')}
                </p>
                <div className="interactive-demo-cta-buttons">
                    <button 
                        className="cta-primary"
                        onClick={handlePlayFull}
                    >
                        <Icon name="play" />
                        {t('demo.continueStory')}
                    </button>
                    <button 
                        className="cta-outline"
                        onClick={() => navigate('/SelectStory')}
                    >
                        {t('demo.exploreOther')}
                    </button>
                </div>
            </div>

            {/* Demo limit modal */}
            {showLimitModal && (
                <div className="interactive-demo-limit-modal">
                    <div className="interactive-demo-limit-content">
                        <Icon name="lock" size="huge" />
                        <h3>{t('demo.limitReached')}</h3>
                        <p>{t('demo.limitMessage')}</p>
                        <div className="interactive-demo-limit-buttons">
                            <button 
                                className="cta-primary"
                                onClick={handleSignUp}
                            >
                                {t('demo.signUpFree')}
                            </button>
                            <button 
                                className="cta-secondary"
                                onClick={handlePlayFull}
                            >
                                {t('demo.continueAsGuest')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
